import {
  Box,
  Flex,
  Grid,
  GridItem,
  Heading,
  Skeleton,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { AuthActionTypes, getUser, useAuth } from '@devd-client/devd/auth';
import {
  DateFilter,
  DownloadButton,
  durationData,
  ISelect,
  Loader,
  Paginate,
  PeriodType,
  RepoDto,
  TeamMenu,
  useDashboardStore,
  useToastHook,
} from '@devd-client/devd/components';
import { Select } from 'chakra-react-select';
import moment from 'moment';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useReworkDensityGql } from '../../graphql';
import {
  downloadReworkDensityReport,
  useFetchRepoList,
} from '../../api/endpoints';
import { ReworkDensityItem } from '../../helpers/report.types';
import { ReworkContributorCell } from './ContributorCol';

const PAGE_SIZE = 20;

const GRID_TEMPLATE_COLUMNS =
  'minmax(180px, 1fr) minmax(150px, 160px) minmax(140px, 160px) minmax(160px, 180px)';

const HEADER_BG = '#FBFBFB';
const BORDER = '1px solid #E0E0E0';

const COLUMNS = [
  { key: 'contributor', label: 'Contributor', tooltip: 'GitHub login / name' },
  {
    key: 'totalCommits',
    label: 'Total Commits',
    tooltip: 'Total commits in the period',
  },
  {
    key: 'reworkCommits',
    label: 'Rework Commits',
    tooltip: 'Commits classified as rework',
  },
  {
    key: 'reworkPercentage',
    label: 'Rework %',
    tooltip: 'Percentage of commits that are rework',
  },
] as const;

const renderCell = (
  key: (typeof COLUMNS)[number]['key'],
  row: ReworkDensityItem
) => {
  switch (key) {
    case 'contributor':
      return row.contributor || '—';
    case 'totalCommits':
      return row.totalCommits ?? '—';
    case 'reworkCommits':
      return row.reworkCommits ?? '—';
    case 'reworkPercentage':
      return row.reworkPercentage != null
        ? `${Number(row.reworkPercentage).toFixed(1)}%`
        : '—';
  }
};

export const ReworkDensityTable = () => {
  const { selected, setSelected } = useDashboardStore();
  const [appState, dispatch] = useAuth();
  const [newToast] = useToastHook();
  const [downloadPending, setDownloadPending] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedRepos, setSelectedRepos] = useState<
    { value: string; label: string }[]
  >([]);

  const { data: repoList } = useFetchRepoList('git');

  useEffect(() => {
    if (selected.startDate && selected.endDate) return;
    const sprintPref = appState?.user?.dto?.sprintDetail;
    if (appState?.user?.dto?.sprint && sprintPref) {
      try {
        const parsed = JSON.parse(sprintPref);
        setSelected({
          ...(durationData[6] as PeriodType),
          startDate: moment(parsed?.startDate).format('YYYY-MM-DD'),
          endDate: moment(parsed?.endDate).format('YYYY-MM-DD'),
          sprintId: appState.user.dto.sprint,
          sprintName: parsed?.sprintName,
        });
        return;
      } catch {
        // fall through
      }
    }
    setSelected(durationData[3] as PeriodType);
  }, []);

  const [selectedTeam, setSelectedTeam] = useState<ISelect>({
    value: appState?.user?.dto?.team || '',
    label: appState?.user?.dto?.team || 'Organisation',
  });

  const teamIds = useMemo(
    () =>
      selectedTeam.value && selectedTeam.label !== 'Organisation'
        ? [selectedTeam.value]
        : [],
    [selectedTeam]
  );

  const projectsFilter = useMemo(
    () => selectedRepos.map((r) => r.value),
    [selectedRepos]
  );

  useEffect(() => {
    setCurrentPage(0);
  }, [
    selected.startDate,
    selected.endDate,
    selected.sprintId,
    teamIds,
    projectsFilter,
  ]);

  const { data, isFetching } = useReworkDensityGql(
    {
      startDate: selected.startDate,
      endDate: selected.endDate,
      teamIds,
      sprintId: selected.sprintId || '',
      projectsFilter,
    },
    currentPage,
    PAGE_SIZE
  );

  const rows: ReworkDensityItem[] = useMemo(
    () => data?.data ?? [],
    [data?.data]
  );

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page - 1);
  }, []);

  const handleDownload = useCallback(async () => {
    if (!selected.startDate || !selected.endDate) return;
    setDownloadPending(true);
    try {
      const res = await downloadReworkDensityReport({
        startDate: selected.startDate,
        endDate: selected.endDate,
        teamIds,
        sprintId: selected.sprintId || '',
        projectsFilter,
      });
      newToast({
        message:
          res.dto ||
          'Your request has been received. The rework density xlsx will be sent to your email shortly.',
        status: 'success',
      });
    } catch (err: unknown) {
      const message =
        (err as { message?: string })?.message ||
        'There is some error in downloading data. Please try again or contact support at support@devdynamics.ai';
      newToast({ message, status: 'error' });
    } finally {
      setDownloadPending(false);
    }
  }, [
    newToast,
    selected.startDate,
    selected.endDate,
    selected.sprintId,
    teamIds,
    projectsFilter,
  ]);

  return (
    <Flex direction="column" w="100%">
      <Flex align="center" justify="space-between">
        <Heading as="h1" size="md">
          Rework Density Report
        </Heading>
        <Flex align="center" gap={3}>
          <DownloadButton downloadCSV={handleDownload} />
          <DateFilter
            getUser={getUser}
            appState={appState}
            dispatch={dispatch}
            AuthActionTypes={AuthActionTypes}
          />
        </Flex>
      </Flex>

      <Flex align="center" gap={3} mt={4} flexWrap="wrap">
        <TeamMenu
          selectedTeam={selectedTeam}
          handleSelectedTeam={setSelectedTeam}
          AuthActionTypes={AuthActionTypes}
          getUser={getUser}
          appState={appState}
          dispatch={dispatch}
          hideShowDetails
        />
        <Box minW="220px" maxW="420px">
          <Text
            fontSize="sm"
            fontFamily="heading"
            fontWeight="semibold"
            color="text.secondary"
            lineHeight="17px"
            mb={2}
          >
            Repository
          </Text>
          <Select
            placeholder="All Repositories"
            isMulti
            useBasicStyles
            size="sm"
            value={selectedRepos}
            onChange={(val: any) => setSelectedRepos(val)}
            options={repoList?.dto?.map((repo: RepoDto) => ({
              value: repo.key,
              label: repo.name,
            }))}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{
              menuPortal: (base: object) => ({ ...base, zIndex: 9999 }),
            }}
          />
        </Box>
      </Flex>

      <Box
        mt={3}
        overflowX="auto"
        overflowY="auto"
        border={BORDER}
        height="66vh"
        width="100%"
        borderRadius="8px"
        position="relative"
        bg="white"
      >
        <Grid
          templateColumns={GRID_TEMPLATE_COLUMNS}
          w="max-content"
          minW="100%"
        >
          {/* Header */}
          {COLUMNS.map((col, idx) => (
            <GridItem
              key={`h-${col.key}`}
              position="sticky"
              top={0}
              zIndex={idx === 0 ? 21 : 20}
              left={idx === 0 ? 0 : undefined}
              bg={HEADER_BG}
              minH="52px"
              display="flex"
              alignItems="center"
              px={4}
              py={2}
              borderBottom={BORDER}
              borderLeft={idx > 0 ? BORDER : undefined}
              boxShadow={idx === 0 ? '1px 0 0 0 #E0E0E0' : undefined}
            >
              <Tooltip label={col.tooltip} hasArrow>
                <Text
                  fontSize="sm"
                  fontFamily="heading"
                  fontWeight="medium"
                  color="text.secondary2"
                  noOfLines={1}
                >
                  {col.label}
                </Text>
              </Tooltip>
            </GridItem>
          ))}

          {/* Rows */}
          {isFetching
            ? [...Array(PAGE_SIZE)].map((_, rowIdx) => (
                <Fragment key={`sk-row-${rowIdx}`}>
                  {COLUMNS.map((col, colIdx) => (
                    <GridItem
                      key={`sk-${rowIdx}-${col.key}`}
                      position={colIdx === 0 ? 'sticky' : undefined}
                      left={colIdx === 0 ? 0 : undefined}
                      zIndex={colIdx === 0 ? 10 : undefined}
                      bg="white"
                      minH="52px"
                      display="flex"
                      alignItems="center"
                      px={4}
                      py={3}
                      borderBottom={BORDER}
                      borderLeft={colIdx > 0 ? BORDER : undefined}
                      boxShadow={colIdx === 0 ? '1px 0 0 0 #E0E0E0' : undefined}
                    >
                      <Skeleton
                        height="14px"
                        width={colIdx === 0 ? '160px' : '60px'}
                      />
                    </GridItem>
                  ))}
                </Fragment>
              ))
            : rows.map((row, rowIdx) => (
                <Fragment key={`r-row-${rowIdx}`}>
                  {COLUMNS.map((col, colIdx) => (
                    <GridItem
                      key={`r-${rowIdx}-${col.key}`}
                      position={colIdx === 0 ? 'sticky' : undefined}
                      left={colIdx === 0 ? 0 : undefined}
                      zIndex={colIdx === 0 ? 10 : undefined}
                      bg="white"
                      minH="52px"
                      display="flex"
                      alignItems="center"
                      px={4}
                      py={3}
                      borderBottom={BORDER}
                      borderLeft={colIdx > 0 ? BORDER : undefined}
                      boxShadow={colIdx === 0 ? '1px 0 0 0 #E0E0E0' : undefined}
                    >
                      {colIdx === 0 ? (
                        <ReworkContributorCell
                          contributor={row.contributor || '—'}
                        />
                      ) : (
                        <Text
                          fontSize="sm"
                          fontFamily="heading"
                          color="text.primary"
                          fontWeight="normal"
                          noOfLines={1}
                        >
                          {renderCell(col.key, row)}
                        </Text>
                      )}
                    </GridItem>
                  ))}
                </Fragment>
              ))}
        </Grid>
      </Box>

      <Box marginLeft="-20px">
        <Paginate
          currentPage={(data?.currentPage ?? 0) + 1}
          totalPageCount={data?.totalPages ?? 1}
          pageSize={PAGE_SIZE}
          onPageChange={(page) => handlePageChange(page as number)}
        />
      </Box>

      {downloadPending && <Loader />}
    </Flex>
  );
};

export default ReworkDensityTable;
